const getUTCDateTimestamp = (date: Date): number =>
  Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());

const getEarliestMatchDate = (matches: { matchDate: string }[]): Date => {
  const sorted = [...matches].sort(
    (a, b) => new Date(a.matchDate).getTime() - new Date(b.matchDate).getTime()
  );
  return new Date(sorted[0].matchDate);
};

/**
 * Find the current match group using calendar dates.
 * A group is current from its first match date until the next group's first match date.
 */
export const determineActiveMatchGroup = <T extends { id: string }>(
  groups: T[],
  matches: { matchDate: string; matchGroup: { id: string } }[],
  now: Date = new Date()
): T | null => {
  const today = getUTCDateTimestamp(now);

  const groupMatches = new Map<string, typeof matches>();
  matches.forEach(match => {
    const groupId = match.matchGroup.id;
    if (!groupMatches.has(groupId)) {
      groupMatches.set(groupId, []);
    }
    groupMatches.get(groupId)!.push(match);
  });

  const sortedGroups = groups
    .filter(group => (groupMatches.get(group.id)?.length ?? 0) > 0)
    .sort((a, b) => {
      const aEarliest = getEarliestMatchDate(groupMatches.get(a.id)!);
      const bEarliest = getEarliestMatchDate(groupMatches.get(b.id)!);
      return getUTCDateTimestamp(aEarliest) - getUTCDateTimestamp(bEarliest);
    });

  for (let i = 0; i < sortedGroups.length; i++) {
    const group = sortedGroups[i];
    const nextGroup = sortedGroups[i + 1];
    const groupStart = getUTCDateTimestamp(getEarliestMatchDate(groupMatches.get(group.id)!));

    if (today >= groupStart) {
      if (nextGroup) {
        const nextGroupStart = getUTCDateTimestamp(getEarliestMatchDate(groupMatches.get(nextGroup.id)!));
        if (today < nextGroupStart) {
          return group;
        }
      } else {
        return group;
      }
    }
  }

  return sortedGroups[0] || null;
};
